import { useCallback } from 'react'

import { useBalanceConfig, useBalanceLabels, useTrackBalanceEvent } from '@features/balanceLedger'
import type { BalanceExportFormat } from '@features/balanceLedger/types/balanceExport'
import { useExpertReportPeriod } from '@features/expertReports'
import { useUserOrganization } from '@modules-deprecated/app/organization'

import { SortDirection } from '../../../enums/sortDirection'
import { useSuppliersBalanceFiltersContext } from '../contexts/suppliersBalanceFiltersContext'
import { SuppliersBalanceFilterType } from '../enums/suppliersBalanceFilterType'
import { useExportFileDownload } from './useExportFileDownload'
import { useSuppliersBalanceSort } from './useSuppliersBalanceSort'

interface UseDownloadCreditorBalanceSummaryExportProps {
  // Analytics-only: the account count shown in the overview at the time of export. Optional so existing call sites
  // (and their tests) keep compiling without it.
  accountCount?: number
}

interface UseDownloadCreditorBalanceSummaryExportResult {
  downloadExport: (format: BalanceExportFormat) => Promise<void>
  isExporting: boolean
}

export const useDownloadCreditorBalanceSummaryExport = ({
  accountCount,
}: UseDownloadCreditorBalanceSummaryExportProps = {}): UseDownloadCreditorBalanceSummaryExportResult => {
  const config = useBalanceConfig()
  const { bt } = useBalanceLabels()
  const trackBalanceEvent = useTrackBalanceEvent()
  const { organization } = useUserOrganization()
  const { filters, search } = useSuppliersBalanceFiltersContext()
  const { encodedPeriod, periodType } = useExpertReportPeriod()
  const { direction, sortBy } = useSuppliersBalanceSort()

  const sortDirection = direction === SortDirection.Desc ? 'DESC' : 'ASC'
  const contactIds = filters[SuppliersBalanceFilterType.Account]
  const balanceDirection = filters[SuppliersBalanceFilterType.BalanceDirection]
  const filtered = !!search || !!contactIds?.length || !!balanceDirection

  const fetchExport = useCallback(
    (format: BalanceExportFormat) =>
      config.fetchSummaryExport({
        organizationId: organization?.id ?? '',
        format,
        period: encodedPeriod,
        sortBy,
        sortDirection,
        search,
        contactIds,
        balanceDirection,
      }),
    [balanceDirection, config, contactIds, encodedPeriod, organization?.id, search, sortBy, sortDirection],
  )

  const onExportSuccess = useCallback(
    (format: BalanceExportFormat) => {
      /* eslint-disable @typescript-eslint/naming-convention */
      trackBalanceEvent('Exported', {
        account_count: accountCount,
        export_format: format,
        filtered,
        period_type: periodType,
      })
      /* eslint-enable @typescript-eslint/naming-convention */
    },
    [accountCount, filtered, periodType, trackBalanceEvent],
  )

  const { downloadExport, isExporting } = useExportFileDownload({
    fetchExport,
    isReady: !!organization?.id,
    notificationKey: config.exportNotificationKey,
    errorMessage: bt('export.error'),
    onSuccess: onExportSuccess,
  })

  return { downloadExport, isExporting }
}
